import backpackSdk from "@src/global_functions/backpackSdk/sdk";
import { truthy } from "@src/global_functions/util";
import {
  useQueryDesignDocGroups,
  useQueryDesignDocs,
} from "@src/global_functions/postgrestApi/designDocsQueries";
import { useMemo } from "react";
import {
  createDocumentGroups,
  formatDesignDocs,
  formatEquipmentDocsAsDesignDocs,
  formatProjectAttachmentsAsDesignDocs,
} from "./documentQueriesHelperFns";
import { isDesignDocWithUtilityBillCategory } from "./nonStandardDocumentUtils";

function useQueryEquipmentDocsAsDesignDocs(siteId: number) {
  return backpackSdk.equipment.attachments.useQueryAttachmentInfo({
    siteId,
    select: formatEquipmentDocsAsDesignDocs,
  });
}

function useQueryProjectAttachmentsAsDesignDocs(siteId: number) {
  return backpackSdk.projects.project.attachments.useQueryBackpackProjectAttachmentInformation(
    { site_id: siteId },
    (docs) => formatProjectAttachmentsAsDesignDocs(docs, siteId)
  );
}

function useQueryDesignDocsWithUtilityCategoryMutation(siteId: number) {
  return useQueryDesignDocs(siteId, formatDesignDocs);
}

export function useQueryAllDocuments(siteId: number) {
  const equipmentDocsQuery = useQueryEquipmentDocsAsDesignDocs(siteId);
  const projectAttachmentsQuery =
    useQueryProjectAttachmentsAsDesignDocs(siteId);
  const designDocsQuery = useQueryDesignDocsWithUtilityCategoryMutation(siteId);

  const allQueries = useMemo(
    () => [equipmentDocsQuery, designDocsQuery, projectAttachmentsQuery],
    [equipmentDocsQuery, designDocsQuery, projectAttachmentsQuery]
  );
  return useMemo(() => {
    const data = allQueries
      .map((q) => q.data)
      .filter(truthy)
      .flat()
      .sort((a, b) => (a?.uploadedAt ?? "").localeCompare(b?.uploadedAt ?? ""));

    return {
      isLoading: allQueries.some((q) => q.isLoading),
      isError: allQueries.some((q) => q.isError),
      data: data.length ? data : [],
      // only need to refetch design docs since only deisgn docs can be uploaded from the documents page.
      refetchDesignDocs: designDocsQuery.refetch,
    };
  }, [allQueries, designDocsQuery.refetch]);
}

export function useQueryDocumentGroups(siteId: number) {
  const equipmentDocsQuery = useQueryEquipmentDocsAsDesignDocs(siteId);
  const projectAttachmentsQuery =
    useQueryProjectAttachmentsAsDesignDocs(siteId);
  const designDocsQuery = useQueryDesignDocsWithUtilityCategoryMutation(siteId);
  const designDocGroupsQuery = useQueryDesignDocGroups(siteId);

  const hasEquipmentDocs = Boolean(equipmentDocsQuery.data?.length);
  const hasProjectAttachments = Boolean(projectAttachmentsQuery.data?.length);
  const hasDesignDocsWithUtilityBillCategory = Boolean(
    designDocsQuery.data?.some(isDesignDocWithUtilityBillCategory)
  );

  const allQueries = useMemo(
    () => [equipmentDocsQuery, designDocGroupsQuery, projectAttachmentsQuery],
    [equipmentDocsQuery, designDocGroupsQuery, projectAttachmentsQuery]
  );

  const data = useMemo(() => {
    return [
      ...createDocumentGroups({
        siteId,
        addEquipmentGroup: hasEquipmentDocs,
        addUtilityBillGroup: hasDesignDocsWithUtilityBillCategory,
        addProjectAttachmentGroup: hasProjectAttachments,
        designDocGroups: designDocGroupsQuery.data ?? [],
      }),
    ];
  }, [
    designDocGroupsQuery.data,
    hasEquipmentDocs,
    hasProjectAttachments,
    hasDesignDocsWithUtilityBillCategory,
    siteId,
  ]);

  return useMemo(() => {
    return {
      isLoading: allQueries.some((q) => q.isLoading),
      isError: allQueries.some((q) => q.isError),
      data: data.length ? data : undefined,
      refetchDesignDocGroups: designDocGroupsQuery.refetch,
    };
  }, [allQueries, data, designDocGroupsQuery.refetch]);
}
